/**
 * Catches a render error and says what it was.
 *
 * Written because a screen went blank and there was nothing to go on — no message on
 * the page, nothing in the response, and a server-side check of the API and permissions
 * showed both were fine. React unmounts the subtree on an uncaught error and renders
 * nothing in its place, so "it's just empty" was the only symptom available.
 *
 * A boundary per route means one broken screen no longer takes the shell with it: the
 * navigation still works, and the failure names itself.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Card, PageHeader } from './ui.tsx';

interface Props {
  children: ReactNode;
  /** Named so the message can say which screen failed. */
  area?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept for the browser console, which is where someone helping remotely will look.
    // Not sent anywhere: there is no error-reporting service, and adding one would be a
    // third-party dependency and a data-protection question, not a quick win.
    console.error(`[${this.props.area ?? 'app'}] render failed`, error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  override render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <>
        <PageHeader title={this.props.area ?? 'Something failed'} />
        <Card title="This screen could not be displayed">
          <p className="text-[13px] text-[var(--ink-secondary)]">
            Nothing has been lost and no data was changed. The rest of the system is
            unaffected — use the navigation to go elsewhere.
          </p>

          <p className="mt-3 text-[13px] text-[var(--ink-secondary)]">
            If you are reporting this, the line below is the useful part.
          </p>

          <pre className="mt-2 overflow-x-auto rounded-md border border-[var(--border-default)] bg-[var(--surface-2)] p-3 text-[12px] text-[var(--ink-primary)]">
            {error.name}: {error.message}
            {componentStack ? `\n${componentStack.split('\n').slice(0, 6).join('\n')}` : ''}
          </pre>

          <div className="mt-3 flex gap-2">
            <Button
              variant="primary"
              onClick={() => this.setState({ error: null, componentStack: null })}
            >
              Try again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload the page</Button>
          </div>
        </Card>
      </>
    );
  }
}
